import React, { useEffect, useMemo, useState } from 'react';
import Modal from 'react-modal';
import { LuCloudUpload, LuUserPlus, LuX } from 'react-icons/lu';
import {
  FaCheckDouble,
  FaExclamationTriangle,
  FaFileInvoiceDollar,
  FaShieldAlt,
} from 'react-icons/fa';
import PostLoginLoader from './PostLoginLoader';
import {
  ASSET_TYPES,
  PROVIDER_OTHER,
  getProvidersForAssetType,
  getNomineeContactKey,
  getNomineeOptionLabel,
  getUniqueNominees,
  validateAssetType,
  validateContactRequirement,
  validateFile,
  validateNomineeName,
  validateOptionalEmail,
  validateOptionalPhone,
  validatePolicyNumber,
  validateProviderName,
} from '../config/nomineeForm';

const EMPTY_FORM = {
  nomineeMode: 'new',
  existingNomineeKey: '',
  nomineeName: '',
  nomineeEmail: '',
  nomineePhone: '',
  assetType: '',
  providerPreset: '',
  providerName: '',
  policyNumber: '',
  policyDocument: null,
};

const EMPTY_ERRORS = {
  nomineeName: '',
  nomineeEmail: '',
  nomineePhone: '',
  contact: '',
  assetType: '',
  providerName: '',
  policyNumber: '',
  policyDocument: '',
};

function AddNomineeModal({
  isOpen,
  onClose,
  onSubmit,
  submitLoading,
  nominees = [],
}) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState(EMPTY_ERRORS);

  const existingNominees = useMemo(() => getUniqueNominees(nominees), [nominees]);
  const providerOptions = useMemo(
    () => getProvidersForAssetType(formData.assetType),
    [formData.assetType]
  );

  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setFormData(EMPTY_FORM);
      setErrors(EMPTY_ERRORS);
    }
  }, [isOpen]);

  useEffect(() => {
    document.body.classList.toggle('post-login-active', submitLoading);
    return () => document.body.classList.remove('post-login-active');
  }, [submitLoading]);

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field] || (field === 'nomineeEmail' || field === 'nomineePhone') && errors.contact) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
        contact: field === 'nomineeEmail' || field === 'nomineePhone' ? '' : prev.contact,
      }));
    }
  };

  const handleNomineeModeChange = (mode) => {
    setFormData((prev) => {
      if (mode !== 'existing') {
        return {
          ...prev,
          nomineeMode: mode,
          existingNomineeKey: '',
        };
      }

      const autoKey = prev.existingNomineeKey
        || (existingNominees.length === 1 ? getNomineeContactKey(existingNominees[0]) : '');
      const selected = existingNominees.find((item) => getNomineeContactKey(item) === autoKey);

      return {
        ...prev,
        nomineeMode: mode,
        existingNomineeKey: autoKey,
        nomineeName: selected?.nomineeName || '',
        nomineeEmail: selected?.nomineeEmail || '',
        nomineePhone: selected?.nomineePhone || '',
      };
    });
    setErrors(EMPTY_ERRORS);
  };

  const handleExistingNomineeChange = (key) => {
    const selected = existingNominees.find((item) => getNomineeContactKey(item) === key);
    setFormData((prev) => ({
      ...prev,
      existingNomineeKey: key,
      nomineeName: selected?.nomineeName || '',
      nomineeEmail: selected?.nomineeEmail || '',
      nomineePhone: selected?.nomineePhone || '',
    }));
    setErrors(EMPTY_ERRORS);
  };

  const handleProviderPresetChange = (preset) => {
    setFormData((prev) => ({
      ...prev,
      providerPreset: preset,
      providerName: preset === PROVIDER_OTHER ? prev.providerName : preset,
    }));
    if (errors.providerName) {
      setErrors((prev) => ({ ...prev, providerName: '' }));
    }
  };

  const handleAssetTypeChange = (assetType) => {
    const nextProviders = getProvidersForAssetType(assetType);
    setFormData((prev) => {
      const keepPreset = nextProviders.includes(prev.providerPreset) ? prev.providerPreset : '';
      return {
        ...prev,
        assetType,
        providerPreset: keepPreset,
        providerName: keepPreset === PROVIDER_OTHER ? prev.providerName : '',
      };
    });
    if (errors.assetType || errors.providerName) {
      setErrors((prev) => ({ ...prev, assetType: '', providerName: '' }));
    }
  };

  const validateStepOne = () => {
    const nextErrors = { ...EMPTY_ERRORS };

    if (formData.nomineeMode === 'existing') {
      if (!formData.existingNomineeKey) {
        nextErrors.contact = 'Select an existing nominee or choose to add a new one.';
      } else {
        const contactError = validateContactRequirement(formData.nomineeEmail, formData.nomineePhone);
        if (contactError) {
          nextErrors.contact = 'Selected nominee must have at least an email or phone on file.';
        }
      }
    } else {
      nextErrors.nomineeName = validateNomineeName(formData.nomineeName);
      nextErrors.nomineeEmail = validateOptionalEmail(formData.nomineeEmail);
      nextErrors.nomineePhone = validateOptionalPhone(formData.nomineePhone);
      nextErrors.contact = validateContactRequirement(formData.nomineeEmail, formData.nomineePhone);
    }

    setErrors(nextErrors);
    return !Object.values(nextErrors).some(Boolean);
  };

  const validateStepTwo = () => {
    const providerValue = formData.providerPreset === PROVIDER_OTHER
      ? formData.providerName
      : formData.providerPreset;
    const nextErrors = {
      ...EMPTY_ERRORS,
      assetType: validateAssetType(formData.assetType),
      providerName: validateProviderName(providerValue),
      policyNumber: validatePolicyNumber(formData.policyNumber),
      policyDocument: validateFile(formData.policyDocument, true),
    };
    setErrors(nextErrors);
    return !Object.values(nextErrors).some(Boolean);
  };

  const handleNextStep = (event) => {
    event.preventDefault();
    if (validateStepOne()) {
      setStep(2);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateStepTwo()) return;

    const providerValue = formData.providerPreset === PROVIDER_OTHER
      ? formData.providerName.trim()
      : formData.providerPreset;
    const existingNominee = existingNominees.find(
      (item) => getNomineeContactKey(item) === formData.existingNomineeKey
    );
    const fallbackName = formData.nomineeName.trim()
      || existingNominee?.nomineeName?.trim()
      || existingNominee?.nomineeEmail?.split('@')[0]
      || 'Nominee';

    await onSubmit({
      nomineeName: formData.nomineeName.trim()
        || fallbackName
        || formData.nomineeEmail.split('@')[0]
        || 'Nominee',
      nomineeEmail: formData.nomineeEmail.trim().toLowerCase(),
      nomineePhone: formData.nomineePhone.trim(),
      assetType: formData.assetType,
      providerName: providerValue,
      policyNumber: formData.policyNumber.trim(),
      policyDocument: formData.policyDocument,
    });
  };

  const selectedExisting = existingNominees.find(
    (item) => getNomineeContactKey(item) === formData.existingNomineeKey
  );

  return (
    <>
      {submitLoading ? (
        <PostLoginLoader message="Adding nominee & asset…" />
      ) : null}
      <Modal
        isOpen={isOpen}
        onRequestClose={onClose}
        contentLabel="Add nominee and asset"
        className="modal modal--wizard"
        overlayClassName="modal-overlay"
      >
      <div className="modal-accent" aria-hidden="true" />
      <button
        type="button"
        className="modal-close-btn"
        onClick={onClose}
        aria-label="Close add nominee dialog"
        disabled={submitLoading}
      >
        <LuX aria-hidden="true" />
      </button>

      <div className="modal-header">
        <div className="modal-icon-badge modal-icon-badge--add">
          <LuUserPlus aria-hidden="true" />
        </div>
        <div className="modal-header-text">
          <h2>Add nominee &amp; asset</h2>
          <p>Register who should be notified and upload the related policy or asset document.</p>
        </div>
      </div>

      <div className="modal-step-indicator" aria-label="Form progress">
        <div className={`modal-step ${step >= 1 ? 'active' : ''}`}>
          <span>1</span>
          <strong>Nominee</strong>
        </div>
        <div className="modal-step-line" aria-hidden="true" />
        <div className={`modal-step ${step >= 2 ? 'active' : ''}`}>
          <span>2</span>
          <strong>Asset</strong>
        </div>
      </div>

      {step === 1 ? (
        <form onSubmit={handleNextStep} className="modal-form">
          {existingNominees.length > 0 ? (
            <div className="form-field">
              <span className="form-label">Nominee selection</span>
              <div className="nominee-mode-toggle" role="tablist" aria-label="Nominee selection mode">
                <button
                  type="button"
                  role="tab"
                  aria-selected={formData.nomineeMode === 'new'}
                  className={`nominee-mode-btn ${formData.nomineeMode === 'new' ? 'active' : ''}`}
                  onClick={() => handleNomineeModeChange('new')}
                >
                  Add new nominee
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={formData.nomineeMode === 'existing'}
                  className={`nominee-mode-btn ${formData.nomineeMode === 'existing' ? 'active' : ''}`}
                  onClick={() => handleNomineeModeChange('existing')}
                >
                  Use existing nominee
                </button>
              </div>
            </div>
          ) : null}

          {formData.nomineeMode === 'existing' && existingNominees.length > 0 ? (
            <div className="form-field">
              <label className="form-label" htmlFor="existing-nominee-select">
                Existing nominee <span className="required-mark">*</span>
              </label>
              <select
                id="existing-nominee-select"
                className={`modal-select modal-select--nominee ${errors.contact ? 'input-error' : ''}`}
                value={formData.existingNomineeKey}
                onChange={(e) => handleExistingNomineeChange(e.target.value)}
                disabled={submitLoading}
              >
                <option value="">Select nominee</option>
                {existingNominees.map((nominee) => {
                  const key = getNomineeContactKey(nominee);
                  return (
                    <option key={key} value={key}>
                      {getNomineeOptionLabel(nominee)}
                    </option>
                  );
                })}
              </select>
              {selectedExisting ? (
                <div className="nominee-summary-card">
                  {selectedExisting.nomineeName ? (
                    <strong>{selectedExisting.nomineeName}</strong>
                  ) : null}
                  <dl className="nominee-summary-details">
                    <div className="nominee-summary-row">
                      <dt>Email</dt>
                      <dd>{selectedExisting.nomineeEmail || 'Not provided'}</dd>
                    </div>
                    <div className="nominee-summary-row">
                      <dt>Phone</dt>
                      <dd>{selectedExisting.nomineePhone || 'Not provided'}</dd>
                    </div>
                    {selectedExisting.assetCount > 1 ? (
                      <div className="nominee-summary-row">
                        <dt>Assets on file</dt>
                        <dd>{selectedExisting.assetCount}</dd>
                      </div>
                    ) : null}
                  </dl>
                </div>
              ) : (
                <p className="field-helper">
                  Options include email and phone so similar contacts are easy to tell apart.
                </p>
              )}
              {errors.contact ? (
                <div className="error-message">
                  <FaExclamationTriangle />
                  {errors.contact}
                </div>
              ) : null}
            </div>
          ) : (
            <>
              <div className="form-field">
                <label className="form-label" htmlFor="nominee-name">
                  Nominee full name <span className="required-mark">*</span>
                </label>
                <input
                  id="nominee-name"
                  type="text"
                  placeholder="e.g. Priya Sharma"
                  className={errors.nomineeName ? 'input-error' : ''}
                  value={formData.nomineeName}
                  onChange={(e) => handleFieldChange('nomineeName', e.target.value)}
                  maxLength={80}
                  disabled={submitLoading}
                />
                <p className="field-helper">This is how the nominee will appear on your dashboard.</p>
                {errors.nomineeName ? (
                  <div className="error-message">
                    <FaExclamationTriangle />
                    {errors.nomineeName}
                  </div>
                ) : null}
              </div>

              <div className="form-field">
                <label className="form-label" htmlFor="nominee-email">
                  Email address
                </label>
                <input
                  id="nominee-email"
                  type="email"
                  placeholder="e.g. john.doe@example.com"
                  className={errors.nomineeEmail ? 'input-error' : ''}
                  value={formData.nomineeEmail}
                  onChange={(e) => handleFieldChange('nomineeEmail', e.target.value)}
                  maxLength={100}
                  disabled={submitLoading}
                />
                <p className="field-helper">Provide email, phone, or both. At least one contact method is required.</p>
                {errors.nomineeEmail ? (
                  <div className="error-message">
                    <FaExclamationTriangle />
                    {errors.nomineeEmail}
                  </div>
                ) : null}
              </div>

              <div className="form-field">
                <label className="form-label" htmlFor="nominee-phone">
                  Phone number
                </label>
                <input
                  id="nominee-phone"
                  type="tel"
                  placeholder="e.g. 9876543210"
                  className={errors.nomineePhone ? 'input-error' : ''}
                  value={formData.nomineePhone}
                  onChange={(e) => handleFieldChange('nomineePhone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                  maxLength={10}
                  disabled={submitLoading}
                />
                <p className="field-helper">10-digit Indian mobile number without +91 or leading 0.</p>
                {errors.nomineePhone ? (
                  <div className="error-message">
                    <FaExclamationTriangle />
                    {errors.nomineePhone}
                  </div>
                ) : null}
              </div>

              {errors.contact ? (
                <div className="error-message">
                  <FaExclamationTriangle />
                  {errors.contact}
                </div>
              ) : null}
            </>
          )}

          <div className="modal-buttons">
            <button type="submit" className="add-btn" disabled={submitLoading}>
              Continue to asset
            </button>
            <button type="button" className="cancel-btn" onClick={onClose} disabled={submitLoading}>
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-field">
            <label className="form-label" htmlFor="asset-type">
              Asset type <span className="required-mark">*</span>
            </label>
            <select
              id="asset-type"
              className={`modal-select ${errors.assetType ? 'input-error' : ''}`}
              value={formData.assetType}
              onChange={(e) => handleAssetTypeChange(e.target.value)}
              disabled={submitLoading}
            >
              <option value="">Select asset type</option>
              {ASSET_TYPES.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            {errors.assetType ? (
              <div className="error-message">
                <FaExclamationTriangle />
                {errors.assetType}
              </div>
            ) : null}
          </div>

          <div className="form-field">
            <label className="form-label" htmlFor="provider-preset">
              Provider / institution <span className="required-mark">*</span>
            </label>
            <select
              id="provider-preset"
              className={`modal-select ${errors.providerName ? 'input-error' : ''}`}
              value={formData.providerPreset}
              onChange={(e) => handleProviderPresetChange(e.target.value)}
              disabled={submitLoading || !formData.assetType}
            >
              <option value="">
                {formData.assetType ? 'Select provider' : 'Select asset type first'}
              </option>
              {providerOptions.map((preset) => (
                <option key={preset} value={preset}>{preset}</option>
              ))}
            </select>
            {!formData.assetType ? (
              <p className="field-helper">Choose an asset type to see relevant providers.</p>
            ) : null}
            {formData.providerPreset === PROVIDER_OTHER ? (
              <input
                type="text"
                placeholder="Enter provider name"
                className={errors.providerName ? 'input-error' : ''}
                value={formData.providerName}
                onChange={(e) => handleFieldChange('providerName', e.target.value)}
                maxLength={50}
                disabled={submitLoading}
              />
            ) : null}
            {errors.providerName ? (
              <div className="error-message">
                <FaExclamationTriangle />
                {errors.providerName}
              </div>
            ) : null}
          </div>

          <div className="form-field">
            <label className="form-label" htmlFor="policy-number">
              Policy / account number
            </label>
            <input
              id="policy-number"
              type="text"
              placeholder="Optional but recommended"
              className={errors.policyNumber ? 'input-error' : ''}
              value={formData.policyNumber}
              onChange={(e) => handleFieldChange('policyNumber', e.target.value)}
              maxLength={40}
              disabled={submitLoading}
            />
            {errors.policyNumber ? (
              <div className="error-message">
                <FaExclamationTriangle />
                {errors.policyNumber}
              </div>
            ) : null}
          </div>

          <div className="form-field">
            <span className="form-label">
              Policy / asset document <span className="required-mark">*</span>
            </span>
            <label className="modal-file-upload">
              <input
                type="file"
                accept=".pdf,image/jpeg,image/png,image/jpg"
                onChange={(e) => handleFieldChange('policyDocument', e.target.files[0] || null)}
                disabled={submitLoading}
              />
              <LuCloudUpload className="upload-icon" aria-hidden="true" />
              <span className="upload-text">
                {formData.policyDocument ? formData.policyDocument.name : 'Choose document'}
              </span>
              <span className="upload-hint">PDF, JPG or PNG · Max 2MB</span>
            </label>
            <p className="field-helper field-helper--secure">
              <FaShieldAlt aria-hidden="true" />
              Documents are stored securely and shared only after verification.
            </p>
            {formData.policyDocument ? (
              <div className="file-preview">
                <FaFileInvoiceDollar aria-hidden="true" />
                <span className="file-preview-name">{formData.policyDocument.name}</span>
                <span className="file-preview-size">
                  ({(formData.policyDocument.size / 1024 / 1024).toFixed(2)} MB)
                </span>
              </div>
            ) : null}
            {errors.policyDocument ? (
              <div className="error-message">
                <FaExclamationTriangle />
                {errors.policyDocument}
              </div>
            ) : null}
          </div>

          <div className="modal-buttons">
            <button type="submit" className="add-btn" disabled={submitLoading}>
              <FaCheckDouble />
              Save nominee &amp; asset
            </button>
            <button
              type="button"
              className="cancel-btn"
              onClick={() => setStep(1)}
              disabled={submitLoading}
            >
              Back
            </button>
          </div>
        </form>
      )}
      </Modal>
    </>
  );
}

export default AddNomineeModal;
